/**
 * AudioStatusIndicator — small badge showing audio engine state.
 *
 * Displayed in the app header. Tells users when piano samples are loading,
 * when the synth fallback is in use, etc.
 */

import { type ReactNode } from "react";

export type AudioStatus =
  | "ready"
  | "loading"
  | "synth-fallback"
  | "error";

export interface AudioStatusIndicatorProps {
  status: AudioStatus;
  /** Optional progress for "loading" state, 0..1. */
  progress?: number;
  /** Currently active instrument label. */
  instrumentLabel?: string;
}

export function AudioStatusIndicator({
  status,
  progress,
  instrumentLabel,
}: AudioStatusIndicatorProps): ReactNode {
  const cfg = STATUS_CONFIG[status];
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-mono ${cfg.bg} ${cfg.text}`}
      role="status"
      aria-live="polite"
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} aria-hidden />
      <span>
        {cfg.label}
        {instrumentLabel && status === "ready" && ` · ${instrumentLabel}`}
        {progress !== undefined && (status === "loading" || status === "synth-fallback") &&
          ` · ${Math.round(progress * 100)}%`}
      </span>
    </div>
  );
}

const STATUS_CONFIG: Record<AudioStatus, { label: string; bg: string; text: string; dot: string }> = {
  ready: {
    label: "audio ready",
    bg: "bg-success/10",
    text: "text-success",
    dot: "bg-success",
  },
  loading: {
    label: "loading samples",
    bg: "bg-info/10",
    text: "text-info",
    dot: "bg-info motion-safe:animate-pulse",
  },
  "synth-fallback": {
    label: "using synth",
    bg: "bg-warning/10",
    text: "text-warning",
    dot: "bg-warning",
  },
  error: {
    label: "audio error",
    bg: "bg-danger/10",
    text: "text-danger",
    dot: "bg-danger",
  },
};
